<main class="shell">
  @if (screen() === 'loading') {
    <section class="card compact state-card" aria-live="polite">
      <span class="state-icon" aria-hidden="true">🌱</span>
      <p>Sto preparando il giardino…</p>
    </section>
  }

  @if (screen() === 'error') {
    <section class="card compact state-card">
      <span class="state-icon" aria-hidden="true">🌧️</span>
      <h1>Non riesco a salvare sul dispositivo</h1>
      <p>Ricarica la pagina. L'app non invia questi dati a un server.</p>
    </section>
  }

  @if (screen() === 'curriculum') {
    <section class="card adult-card curriculum-card">
      <header class="adult-heading">
        <p class="eyebrow">Area adulto · curriculum verificato</p>
        <h1>Programma e fonti</h1>
      </header>
      <p class="curriculum-intro">
        Le skill canoniche devono essere allineate a fonti istituzionali. Il MIM definisce il vincolo nazionale;
        quando serve una granularità specifica per la prima primaria usiamo un benchmark grade-specific ufficiale,
        senza presentarlo come programma ministeriale italiano.
      </p>

      <section class="source-card">
        <p class="eyebrow">0–3 · fonti istituzionali vincolanti</p>
        <h2>Prima infanzia</h2>
        <p>
          Le attività 0–3 hanno un gate separato: devono dichiarare fascia, obiettivo, sicurezza, modalità caregiver,
          durata sullo schermo e riferimento puntuale a una fonte ammessa. I risultati non vengono usati per diagnosi o screening.
        </p>
        <div class="source-list">
          <article class="source-card">
            <p class="source-kind">Italia · cornice nazionale 0–3</p>
            <h3>Ministero dell’Istruuzione</h3>
            <p><strong>D.M. 24 febbraio 2022, n. 43</strong></p>
            <p>Orientamenti nazionali per i servizi educativi per l’infanzia.</p>
            <a href="https://www.istruzione.it/sistema-integrato-06/orientamenti-nazionali.html" target="_blank" rel="noopener noreferrer">Apri la fonte ufficiale</a>
          </article>
          <article class="source-card">
            <p class="source-kind">Italia · cornice pedagogica zerosei</p>
            <h3>Ministero dell’Istruzione</h3>
            <p><strong>D.M. 22 novembre 2021, n. 334</strong></p>
            <p>Linee pedagogiche per il sistema integrato zerosei.</p>
            <a href="https://www.istruzione.it/sistema-integrato-06/allegati/decreto%20ministeriale%2022%20novembre%202021,%20n.%20334.pdf" target="_blank" rel="noopener noreferrer">Apri la fonte ufficiale</a>
          </article>
          <article class="source-card">
            <p class="source-kind">Protocollo operativo · responsive caregiving</p>
            <h3>WHO / UNICEF</h3>
            <p><strong>Care for Child Development · ISBN 9789241548403</strong></p>
            <p>Raccomandazioni operative di gioco e comunicazione suddivise per fascia d’età.</p>
            <a href="https://www.who.int/publications/i/item/9789241548403" target="_blank" rel="noopener noreferrer">Apri la fonte ufficiale</a>
          </article>
          <article class="source-card">
            <p class="source-kind">Guardrail sanitario · schermo e sedentarietà</p>
            <h3>World Health Organization</h3>
            <p><strong>Under-5 guidelines · ISBN 9789241550536</strong></p>
            <p>Vincoli su attività fisica, sedentarietà, sonno e tempo di schermo nella prima infanzia.</p>
            <a href="https://www.who.int/publications/i/item/9789241550536" target="_blank" rel="noopener noreferrer">Apri la fonte ufficiale</a>
          </article>
        </div>
      </section>

      <div class="curriculum-skills">
        @for (skill of curriculumSkills; track skill.id) {
          <article class="curriculum-skill">
            <div class="curriculum-skill-heading">
              <div>
                <p class="eyebrow">{{ skill.domain }}</p>
                <h2>{{ skill.title }}</h2>
              </div>
              <span class="status-badge">{{ skill.status === 'implemented' ? 'Implementata' : skill.status }}</span>
            </div>
            <p><strong>Livello:</strong> {{ skill.stage }}</p>
            <p><strong>Famiglia esercizi:</strong> {{ skill.exerciseFamily }}</p>

            <div class="source-list">
              @for (mapping of skill.alignments; track mapping.sourceId) {
                @if (sourceFor(mapping.sourceId); as source) {
                  <section class="source-card">
                    <p class="source-kind">
                      {{ source.kind === 'national-curriculum' ? 'Italia · riferimento nazionale' : 'Benchmark globale · Grade 1' }}
                    </p>
                    <h3>{{ source.authority }}</h3>
                    <p><strong>{{ source.reference }}</strong></p>
                    <p>{{ mapping.alignment }}</p>
                    <p class="source-locator">{{ mapping.locator }}</p>
                    <a [href]="source.url" target="_blank" rel="noopener noreferrer">Apri la fonte ufficiale</a>
                  </section>
                }
              }
            </div>
          </article>
        }
      </div>

      <div class="method-note">
        <strong>Regola del progetto:</strong> una skill entra nel percorso canonico solo se ha almeno un allineamento
        istituzionale documentato. Attività non ancora validate restano marcate come sperimentali.
      </div>

      <section class="source-card diagnostic-card">
        <p class="eyebrow">Diagnostica locale</p>
        <h2>Esporta risultati pseudonimizzati</h2>
        <p>
          Genera un JSON per confrontare difficoltà per skill, domanda e profilo senza esportare nomi, età,
          ID dei profili, ID dei tentativi o timestamp dei singoli tentativi. Gli alias dei profili vengono rigenerati a ogni export.
        </p>
        <p class="privacy-note">
          L'export conserva il raggruppamento dei tentativi per profilo per distinguere un problema individuale da un esercizio problematico:
          è quindi pseudonimizzato, non garantito anonimo.
        </p>
        <button class="secondary" type="button" [disabled]="exporting()" (click)="exportDiagnosticData()">
          {{ exporting() ? 'Preparazione…' : 'Esporta JSON diagnostico' }}
        </button>
        @if (exportStatus()) {
          <p aria-live="polite">{{ exportStatus() }}</p>
        }
      </section>

      <div class="actions split-actions">
        <button class="quiet-button" type="button" (click)="openAudioSettings()">Audio e TTS</button>
        <button class="primary" type="button" (click)="closeCurriculum()">Indietro</button>
      </div>
    </section>
  }

  @if (screen() === 'audio-settings') {
    <app-audio-settings-screen
      [diagnostics]="speechDiagnostics()"
      [mode]="speechMode()"
      [busyLocale]="speechBusyLocale()"
      [status]="speechSettingsStatus()"
      (modeChange)="setSpeechMode($event)"
      (prepareVoice)="prepareSpeechVoice($event)"
      (testVoice)="testSpeechVoice($event)"
      (forceDownloadVoice)="forceDownloadSpeechVoice($event)"
      (refresh)="refreshSpeechDiagnostics()"
      (back)="closeAudioSettings()"
    />
  }

  @if (screen() === 'choose-profile') {
    <section class="card child-card profile-card">
      <header class="welcome-heading">
        <span class="garden-mark" aria-hidden="true">🌱</span>
        <div>
          <p class="eyebrow">{{ environment.appName }}</p>
          <h1>Chi gioca oggi?</h1>
        </div>
      </header>

      <div class="profile-list" aria-label="Scegli un profilo">
        @for (profile of profiles(); track profile.id) {
          <div class="profile-row">
            <button class="profile-button" type="button" (click)="selectProfile(profile)">
              <span class="profile-avatar" aria-hidden="true">★</span>
              <span class="profile-copy">
                <strong>{{ profile.nickname }}</strong>
                <span>{{ profileAgeLabel(profile) }}</span>
              </span>
              <span class="profile-go" aria-hidden="true">›</span>
            </button>
            <button class="edit-profile-button" type="button" [attr.aria-label]="'Modifica profilo ' + profile.nickname" (click)="showEditProfile(profile)">
              <span aria-hidden="true">⚙</span>
            </button>
          </div>
        }
      </div>

      <footer class="adult-tools" aria-label="Strumenti per adulti">
        <span class="adult-tools-label">Per gli adulti</span>
        <div class="adult-tools-actions">
          <button class="quiet-button" type="button" (click)="openCurriculum()">Programma e fonti</button>
          <button class="quiet-button" type="button" (click)="openAudioSettings()">Audio e TTS</button>
          <button class="quiet-button" type="button" (click)="showCreateProfile()">Aggiungi bambino</button>
        </div>
      </footer>
    </section>
  }

  @if (screen() === 'create-profile') {
    <section class="card adult-card profile-form-card">
      <header class="adult-heading">
        <p class="eyebrow">Area adulto · profilo locale</p>
        <h1>Nuovo profilo</h1>
        <p>Il nome serve solo per riconoscere il bambino su questo dispositivo.</p>
      </header>

      <label class="field">
        <span>Nome o soprannome</span>
        <input maxlength="24" autocomplete="off" [value]="nickname()" (input)="updateNickname($event)">
      </label>

      <fieldset class="age-picker">
        <legend>Prima infanzia · 0–3</legend>
        <div class="actions-inline">
          @for (option of earlyChildhoodAgeOptions; track option.band) {
            <button
              type="button"
              [class.primary]="earlyAgeBand() === option.band"
              [class.secondary]="earlyAgeBand() !== option.band"
              (click)="selectEarlyAgeBand(option.band)"
            >{{ option.label }}</button>
          }
        </div>
      </fieldset>

      <fieldset class="age-picker">
        <legend>Età scolastica</legend>
        <div class="age-options">
          @for (value of ages; track value) {
            <button type="button" [class.selected]="earlyAgeBand() === null && age() === value" (click)="selectAge(value)">{{ value }}</button>
          }
        </div>
      </fieldset>

      <p class="privacy-note">Per lo 0–3 salviamo solo la fascia utile al protocollo, non la data di nascita. Profilo e progressi restano su questo dispositivo.</p>
      <div class="actions split-actions">
        <div class="adult-tools-actions">
          <button class="quiet-button" type="button" (click)="openCurriculum()">Programma e fonti</button>
          <button class="quiet-button" type="button" (click)="openAudioSettings()">Audio e TTS</button>
        </div>
        <div class="actions-inline">
          @if (profiles().length) {
            <button class="secondary" type="button" (click)="cancelCreateProfile()">Indietro</button>
          }
          <button class="primary" type="button" [disabled]="!canCreateProfile()" (click)="createProfile()">Crea profilo</button>
        </div>
      </div>
    </section>
  }

  @if (screen() === 'edit-profile') {
    @if (editingProfile(); as profile) {
      <section class="card adult-card profile-form-card">
        <header class="adult-heading">
          <p class="eyebrow">Area adulto · profilo locale</p>
          <h1>Modifica {{ profile.nickname }}</h1>
        </header>
        <label class="field">
          <span>Nome o soprannome</span>
          <input maxlength="24" autocomplete="off" [value]="editNickname()" (input)="updateEditNickname($event)">
        </label>

        <fieldset class="age-picker">
          <legend>Prima infanzia · 0–3</legend>
          <div class="actions-inline">
            @for (option of earlyChildhoodAgeOptions; track option.band) {
              <button
                type="button"
                [class.primary]="editEarlyAgeBand() === option.band"
                [class.secondary]="editEarlyAgeBand() !== option.band"
                (click)="selectEditEarlyAgeBand(option.band)"
              >{{ option.label }}</button>
            }
          </div>
        </fieldset>

        <fieldset class="age-picker">
          <legend>Età scolastica</legend>
          <div class="age-options">
            @for (value of ages; track value) {
              <button type="button" [class.selected]="editEarlyAgeBand() === null && editAge() === value" (click)="selectEditAge(value)">{{ value }}</button>
            }
          </div>
        </fieldset>

        <div class="actions">
          <button class="secondary" type="button" [disabled]="saving()" (click)="cancelEditProfile()">Annulla</button>
          <button class="primary" type="button" [disabled]="!canSaveProfile()" (click)="saveProfile()">Salva</button>
        </div>

        <section class="danger-zone">
          <h2>Elimina profilo</h2>
          @if (!deleteConfirmation()) {
            <p>Elimina il profilo e tutti i tentativi e progressi registrati per questo bambino sul dispositivo.</p>
            <button class="danger-button" type="button" [disabled]="saving()" (click)="requestDeleteProfile()">Elimina profilo</button>
          } @else {
            <p><strong>Confermi?</strong> Questa operazione cancella definitivamente il profilo e tutti i suoi tentativi locali.</p>
            <div class="actions-inline">
              <button class="secondary" type="button" [disabled]="saving()" (click)="cancelDeleteProfile()">No, annulla</button>
              <button class="danger-button" type="button" [disabled]="saving()" (click)="deleteProfile()">Sì, elimina tutto</button>
            </div>
          }
        </section>
      </section>
    }
  }

  @if (screen() === 'caregiver-home') {
    @if (activeProfile(); as profile) {
      <section class="card adult-card">
        <header class="adult-heading">
          <p class="eyebrow">Per l’adulto · programma 0–3</p>
          <h1>Attività per {{ profile.nickname }}</h1>
          <p><strong>Fascia:</strong> {{ profileAgeLabel(profile) }}</p>
        </header>

        <div class="method-note">
          <strong>Regola schermo:</strong> {{ profilePolicy(profile).guidance }}
        </div>

        @if (caregiverActivitiesFor(profile); as activities) {
          @if (activities.length) {
            <p>
              Scegli una scheda, prepara il materiale e poi metti da parte il dispositivo. Le attività provengono
              dal registry 0–3 validato automaticamente e non trasformano il gioco in una prova.
            </p>

            <div class="curriculum-skills" aria-label="Catalogo attività 0–3">
              @for (activity of activities; track activity.activityId) {
                <details class="curriculum-skill" name="caregiver-activity">
                  <summary>
                    <div class="curriculum-skill-heading">
                      <div>
                        <p class="eyebrow">{{ activity.developmentalDomain }}</p>
                        <h2>{{ activity.title }}</h2>
                      </div>
                      @if (caregiverActivityStatus(activity.activityId); as status) {
                        <span class="status-badge">
                          {{ status === 'completed' ? 'Completata' : status === 'tried' ? 'Provata' : 'Saltata' }}
                        </span>
                      }
                    </div>
                    <p><strong>Obiettivo:</strong> {{ activity.objective }}</p>
                    <p class="privacy-note">
                      {{ activity.materials.length }} {{ activity.materials.length === 1 ? 'elemento' : 'elementi' }} da preparare ·
                      circa {{ activity.screen.expectedOnScreenSeconds }} secondi di schermo, solo per l’adulto.
                    </p>
                    <strong>Apri la scheda</strong>
                  </summary>

                  <div class="method-note">
                    <strong>Adesso metti da parte il dispositivo:</strong> prepara ciò che serve, svolgi l’attività nel mondo reale
                    e torna qui solo dopo per registrare cosa hai fatto.
                  </div>

                  <h3>Prepara</h3>
                  <ul>
                    @for (material of activity.materials; track material) {
                      <li>{{ material }}</li>
                    }
                  </ul>

                  <h3>Sicurezza</h3>
                  <ul>
                    @for (constraint of activity.safetyConstraints; track constraint) {
                      <li>{{ constraint }}</li>
                    }
                  </ul>

                  <h3>Come adattarla</h3>
                  <p><strong>Se è facile:</strong> {{ activity.adaptation.ifEasy }}</p>
                  <p><strong>Se è difficile:</strong> {{ activity.adaptation.ifHard }}</p>
                  <p><strong>Quando fermarsi:</strong> {{ activity.adaptation.stopRule }}</p>

                  <h3>Cosa osservare, senza valutare</h3>
                  <ul>
                    @for (signal of activity.observationSignals; track signal) {
                      <li>{{ signal }}</li>
                    }
                  </ul>

                  <div class="source-list">
                    @for (mapping of activity.sourceMappings; track mapping.sourceId + mapping.locator) {
                      @if (earlyChildhoodSourceFor(mapping.sourceId); as source) {
                        <section class="source-card">
                          <p class="source-kind">Fonte istituzionale</p>
                          <h3>{{ source.authority }}</h3>
                          <p><strong>{{ source.title }}</strong></p>
                          <p>{{ mapping.rationale }}</p>
                          <p class="source-locator">{{ mapping.locator }}</p>
                          <a [href]="source.url" target="_blank" rel="noopener noreferrer">Apri la fonte ufficiale</a>
                        </section>
                      }
                    }
                  </div>

                  <div class="actions-inline" aria-label="Registra solo l'esito dell'attività">
                    <button class="secondary" type="button" (click)="setCaregiverActivityStatus(activity.activityId, 'tried')">Provata</button>
                    <button class="primary" type="button" (click)="setCaregiverActivityStatus(activity.activityId, 'completed')">Completata</button>
                    <button class="quiet-button" type="button" (click)="setCaregiverActivityStatus(activity.activityId, 'skipped')">Saltata</button>
                  </div>

                  <p class="privacy-note">Per tornare all’elenco, richiudi questa scheda dal suo titolo. Aprendo un’altra attività questa si richiude automaticamente.</p>
                </details>
              }
            </div>
          } @else {
            <p>
              Non ci sono ancora attività pubblicate per questa fascia. Il sistema non propone attività generiche:
              compariranno solo quando avranno superato il gate istituzionale 0–3.
            </p>
          }
        }

        <p class="privacy-note">
          “Provata”, “completata” e “saltata” descrivono solo ciò che l’adulto ha fatto. Non sono una misura dello sviluppo del bambino
          e non vengono interpretate come diagnosi, screening o valutazione formale delle tappe di sviluppo.
        </p>

        <div class="actions split-actions">
          <div class="adult-tools-actions">
            <button class="quiet-button" type="button" (click)="openCurriculum()">Programma e fonti</button>
            <button class="quiet-button" type="button" (click)="openAudioSettings()">Audio e TTS</button>
          </div>
          <button class="secondary" type="button" (click)="chooseAnotherProfile()">Cambia bambino</button>
        </div>
      </section>
    }
  }

  @if (screen() === 'activity-menu') {
    @if (activeProfile(); as profile) {
      <section class="card child-card activity-menu-card">
        <header class="welcome-heading activity-heading">
          <span class="garden-mark" aria-hidden="true">🎲</span>
          <div>
            <p class="eyebrow">{{ profile.nickname }}</p>
            <h1>Scegli un gioco</h1>
          </div>
        </header>

        <div class="activity-sections" aria-label="Scegli un'attività">
          @for (group of activityGroups; track group.key) {
            <section class="activity-section" [attr.aria-labelledby]="'activity-group-' + group.key">
              <header class="activity-section-heading">
                <span class="activity-section-icon" aria-hidden="true">{{ group.icon }}</span>
                <div class="activity-section-copy">
                  <h2 [id]="'activity-group-' + group.key">{{ group.label }}</h2>
                  <span>{{ group.exercises.length }} {{ group.exercises.length === 1 ? 'tipo di gioco' : 'tipi di gioco' }}</span>
                </div>
              </header>

              <div class="activity-grid">
                @for (exercise of group.exercises; track exercise.kind) {
                  <button
                    class="activity-button"
                    type="button"
                    [attr.aria-label]="exercise.title + ', ' + exerciseVolumeLabel(exercise)"
                    (click)="startExercise(exercise.kind)"
                  >
                    <span class="activity-icon" aria-hidden="true">{{ exercise.icon }}</span>
                    <span class="activity-copy">
                      <strong>{{ exercise.title }}</strong>
                      <span class="activity-meta">{{ exerciseVolumeLabel(exercise) }}</span>
                    </span>
                  </button>
                }
              </div>
            </section>
          }
        </div>

        <footer class="adult-tools completion-tools">
          <div class="adult-tools-actions">
            <button class="quiet-button" type="button" (click)="chooseAnotherProfile()">Cambia bambino</button>
            <button class="quiet-button" type="button" (click)="openCurriculum()">Programma e fonti</button>
            <button class="quiet-button" type="button" (click)="openAudioSettings()">Audio e TTS</button>
          </div>
        </footer>
      </section>
    }
  }

  @if (screen() === 'exercise') {
    @if (exerciseScreenView(); as exerciseView) {
      <app-exercise-screen
        [view]="exerciseView"
        (leaveActivity)="chooseAnotherActivity()"
        (listen)="speakInstruction()"
        (answer)="answer($event)"
        (selectOrderTile)="selectOrderTile($event, currentQuestion())"
        (resetOrder)="clearOrderSelection()"
        (next)="nextQuestion()"
      />
    }
  }

  @if (screen() === 'complete') {
    @if (activeProfile(); as profile) {
      <app-complete-screen
        [view]="{
          profileNickname: profile.nickname,
          exerciseTitle: activeExercise().title,
          savedAttempts: lifetimeProgress().attempts
        }"
        (restartExercise)="restartExercise()"
        (chooseAnotherActivity)="chooseAnotherActivity()"
        (openCurriculum)="openCurriculum()"
        (openAudioSettings)="openAudioSettings()"
        (chooseAnotherProfile)="chooseAnotherProfile()"
      />
    }
  }
</main>